import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import routes from 'routes';
import { getClubs, getPendingClubRecordsCount } from 'api/clubApi';
import { logout } from 'api/authApi';
import {
  updateClubs,
  updateSelectedClub,
  updateClubRecordsPendingCount,
  logout as logoutSlice,
} from 'services/user/userSlice';
import {
  getToken,
  setClubRole,
  setUserPermissionLevel,
  setSelectedClubId,
  getSelectedClubId,
} from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';

// Possible resolutions of "which space does this user belong to".
export const ClubStatus = Object.freeze({
  Loading: 'loading',
  HasClubs: 'has-clubs',
  NoClubs: 'no-clubs',
});

// Defense in depth: the server returns only Active memberships from GET /clubs,
// but the club shell must never grant chrome for an Inactive (suspended) club
// even if a stale or cached response includes one. A member inactive in every
// club resolves to NoClubs and gets the personal shell (profile only).
export const filterActiveClubs = (clubs) =>
  (clubs || []).filter((c) => c.participationStatus === 'Active');

// Makes `club` the active club: keeps the token's cached role/permission in sync
// (these gate the nav + routes), remembers the choice for next session, flips the
// redux active club, and refreshes the pending-records badge for that club.
//
// Shared by the initial default-club pick (below) and the club switcher, so both
// run identical logic. Caller handles navigation afterwards.
export const applySelectedClub = async (club, dispatch) => {
  if (!club) return;
  setClubRole(club.userRole);
  setUserPermissionLevel(club.userPermissionLevel);
  setSelectedClubId(club.clubID);
  dispatch(updateSelectedClub(club));

  if (club.userPermissionLevel >= UserPermissionLevel.Official) {
    try {
      const response = await getPendingClubRecordsCount(club.clubID);
      dispatch(updateClubRecordsPendingCount(response?.data?.count ?? 0));
    } catch {
      dispatch(updateClubRecordsPendingCount(0));
    }
  } else {
    dispatch(updateClubRecordsPendingCount(0));
  }
};

// Resolves which club to land in from a freshly fetched list: the one the user
// last had active (if still present), otherwise their first member-or-higher club,
// otherwise the first club.
const resolveDefaultClub = (clubs) => {
  const savedId = getSelectedClubId();
  return (
    (savedId && clubs.find((c) => c.clubID === savedId)) ||
    clubs.find((c) => c.userPermissionLevel >= UserPermissionLevel.Member) ||
    clubs[0]
  );
};

// Loads the caller's clubs into redux once and reports whether they belong to any.
//
// This is the single source of truth shared by AdminLayout (the club shell) and
// PersonalLayout (the personal shell): both pick which chrome to render from the
// SAME resolution, so a club member always gets the club menu + switching and only
// a genuinely club-less platform citizen gets the bare personal shell.
//
// It does NOT redirect — each layout decides what to do with a `no-clubs` result.
//
// Returns one of ClubStatus: Loading | HasClubs | NoClubs.
export const useEnsureClubs = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Already resolved when a club is selected (navigating between pages with redux
  // already populated) — avoids a spinner flash and a redundant fetch.
  const [status, setStatus] = useState(
    selectedClub ? ClubStatus.HasClubs : ClubStatus.Loading
  );

  const ensure = useCallback(async () => {
    const token = getToken();
    if (!token) {
      navigate(routes.auth.login);
      return;
    }
    if (selectedClub) {
      setStatus(ClubStatus.HasClubs);
      return;
    }
    try {
      const response = await getClubs();
      const clubs = filterActiveClubs(response.data);
      if (clubs.length > 0) {
        dispatch(updateClubs(clubs));
        // Land in the club the user last had active (persisted), else the first
        // member-or-higher club — not a guest/visitor club — so a member of club B
        // + visitor of club A lands in B.
        await applySelectedClub(resolveDefaultClub(clubs), dispatch);
        setStatus(ClubStatus.HasClubs);
      } else {
        setStatus(ClubStatus.NoClubs);
      }
    } catch (err) {
      console.log('[useEnsureClubs] Fetch club error: ', err);
      if (err.status === 401) {
        logout().then(() => {
          dispatch(logoutSlice());
          navigate(routes.client.home);
        });
      } else {
        // Couldn't confirm any clubs — degrade to the personal shell rather than
        // spinning forever; the user still lands somewhere usable.
        setStatus(ClubStatus.NoClubs);
      }
    }
  }, [navigate, dispatch, selectedClub]);

  useEffect(() => {
    ensure();
  }, [ensure]);

  return status;
};

export default useEnsureClubs;
